import { useEffect, useMemo, useState } from "react";
import { Contact, Dropdown, Subheader, TableOfContents } from "./FieldComponents";
import Alert from "./Alert";
import { CustomField, FieldGroup } from "./CustomField";
import { CreateContact } from './CreateContact';
import { EditDetail } from './EditDetail';

export const IntakeDetails = ({ caseData, customFields, fieldUpdates, fetchFields, fetchCase, caseTypes, user_id }) => {
    const acceptedFields = [228, 229, 230];
    const unacceptedFields = [221, 222, 223, 224, 225, 226, 227];
    const [fields, setFields] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [alert, setAlert] = useState(null);
    const [dataChanged, setDataChanged] = useState(false);
    const [createContact, setCreateContact] = useState(false);
    const [newContactFieldId, setNewContactFieldId] = useState(null);
    const [editContact, setEditContact] = useState(false);
    const [contactToEdit, setContactToEdit] = useState(null);
    const [leadData, setLeadData] = useState({
        marketing_source: caseData?.lead?.marketing_source,
        marketing_lead: caseData?.lead?.marketing_lead,
        referral_contact: caseData?.lead?.referral?.id,
        db_origination: caseData?.lead?.db_origination?.id,
        lead_id: caseData?.lead?.id,
        case_type_id: caseData?.case?.case_type_id || caseData?.lead?.case_type_id,
        summary: caseData?.lead?.summary || ''
    });
    const [newData, setNewData] = useState({
        228: fieldUpdates?.[228] || null,
        229: fieldUpdates?.[229] || null,
        230: fieldUpdates?.[230] || null,
    });

    const fieldMetaById = useMemo(() => {
        const map = {};
        (customFields || []).forEach(f => {
            map[f.id] = f;
        });
        return map;
    }, [customFields]);

    useEffect(() => {
        setFields(customFields.filter((field) => acceptedFields.includes(field.id)));
    }, [customFields]);

    // Handle case type change without immediate fetch
    const handleCaseTypeChange = (value) => {
        setLeadData({ ...leadData, case_type_id: value });
        setDataChanged(true);
    };

    const handleNewContactCreated = (contact) => {
        if (newContactFieldId !== null && contact?.id) {
            if (newContactFieldId === 'referral_contact') {
                setLeadData({ ...leadData, referral_contact: contact.id });
            } else if (newContactFieldId === 'db_origination') {
                setLeadData({ ...leadData, db_origination: contact.id });
            } else {
                setNewData(prev => ({ ...prev, [newContactFieldId]: contact.id }));
            }
        }
        setCreateContact(false);
        setNewContactFieldId(null);
    };

    const handleEditContact = (contact) => {
        setContactToEdit(contact);
        setEditContact(true);
    };

    const fetchContacts = () => {
        // add it when you remember it
        console.log('Contacts refreshed');
    };

    // Resolve the folder path for intake uploads. Uses case path when case is present, otherwise fall back to lead-scoped folder.
    const resolveFolderPath = () => {
        if (caseData?.case?.id) {
            return `{{Name}}/Intake Details`;
        }
        if (caseData?.lead?.id) {
            return `leads/${caseData.lead.id}/Intake Details`;
        }
        return `{{Name}}/Intake Details`;
    };

    const uploadFiles = async () => {
        const uploads = [];
        const folderPath = resolveFolderPath();
        const updatedFieldValues = {};

        // Walk every field value we have, since the intake-specific `fields` array only holds date fields.
        for (const [fieldId, value] of Object.entries(newData)) {
            if (!value) continue;

            const files = Array.isArray(value) ? value.filter(Boolean) : [value];
            const hasFile = files.some(f => f instanceof File);
            if (!hasFile) continue;

            const fieldKey = Number(fieldId) || fieldId;

            for (const file of files) {
                if (!(file instanceof File)) continue;

                const uploadData = new FormData();
                uploadData.append('file', file);
                if (caseData?.case?.id) uploadData.append('case_id', caseData.case.id);
                if (caseData?.lead?.id) uploadData.append('lead_id', caseData.lead.id);
                uploadData.append('folder_path', folderPath);
                uploadData.append('user_id', user_id);
                uploadData.append('field_id', fieldKey);

                uploads.push(
                    fetch('https://api.casedb.co/s3_upload.php', {
                        method: 'POST',
                        body: uploadData,
                        credentials: 'include'
                    })
                        .then(res => res.json())
                        .then(result => {
                            const uploadedList = Array.isArray(result?.files) ? result.files : [];
                            // Prefer explicit file_id from backend; fall back to id if present.
                            const uploaded = uploadedList.find(f => f.file_id !== undefined) || uploadedList[0];
                            const fileId = uploaded?.file_id ?? uploaded?.id ?? result?.file_id ?? result?.id;

                            if (result?.success && fileId) {
                                updatedFieldValues[fieldKey] = Number(fileId);
                            } else {
                                console.error('Intake upload failed for field', fieldKey, result);
                            }
                        })
                        .catch(err => console.error('Intake upload error for field', fieldKey, err))
                );
            }
        }

        if (uploads.length === 0) return {};

        await Promise.all(uploads);

        if (Object.keys(updatedFieldValues).length > 0) {
            setNewData(prev => ({
                ...prev,
                ...updatedFieldValues
            }));
        }

        return updatedFieldValues;
    };

    const saveData = async () => {
        // Prepare lead data for intake-details.php
        const leadUpdateData = {
            ...leadData,
            referral_contact: typeof leadData.referral_contact === 'object' && leadData.referral_contact !== null
                ? leadData.referral_contact.id
                : leadData.referral_contact,
            db_origination: typeof leadData.db_origination === 'object' && leadData.db_origination !== null
                ? leadData.db_origination.id
                : leadData.db_origination,
        };

        try {
            // First, save lead data if any changes
            console.log('Lead data comparison:', {
                leadUpdateData,
                currentLeadData: {
                    marketing_source: caseData?.lead?.marketing_source,
                    marketing_lead: caseData?.lead?.marketing_lead,
                    referral_contact: caseData?.lead?.referral?.id,
                    db_origination: caseData?.lead?.db_origination?.id,
                    case_type_id: caseData?.case?.case_type_id || caseData?.lead?.case_type_id
                }
            });

            // Check if lead data has changed by comparing the relevant fields
            const currentReferralId = caseData?.lead?.referral?.id || caseData?.lead?.referral_contact;
            const currentDbOriginationId = caseData?.lead?.db_origination?.id || caseData?.lead?.db_origination;
            const currentMarketingSource = caseData?.lead?.marketing_source;
            const currentMarketingLead = caseData?.lead?.marketing_lead;
            const currentCaseTypeId = caseData?.case?.case_type_id || caseData?.lead?.case_type_id;
            const currentSummary = caseData?.lead?.summary;

            const hasLeadDataChanged = 
                leadUpdateData.marketing_source !== currentMarketingSource ||
                leadUpdateData.marketing_lead !== currentMarketingLead ||
                leadUpdateData.referral_contact !== currentReferralId ||
                leadUpdateData.db_origination !== currentDbOriginationId ||
                leadUpdateData.case_type_id !== currentCaseTypeId ||
                leadUpdateData.summary !== currentSummary;

            if (hasLeadDataChanged) {
                console.log('Sending lead data to intake-details.php:', leadUpdateData);
                const leadResponse = await fetch('https://api.casedb.co/intake-details.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(leadUpdateData),
                    credentials: 'include'
                });
                
                const leadResult = await leadResponse.json();
                if (!leadResponse.ok) {
                    console.error('Error saving lead data:', leadResult);
                    return;
                }
            }

            // Upload any files (returns file_ids into newData)
            const uploadedFieldValues = await uploadFiles();

            // After uploads, persist both normal field values and any uploaded file_ids
            const fieldValues = {};

            // Include non-file values from newData
            Object.keys(newData).forEach(fieldId => {
                const value = newData[fieldId];
                if (value === undefined) return;

                const meta = fieldMetaById[fieldId] || fieldMetaById[Number(fieldId)];
                const isFileField = meta?.field_id === 15 || meta?.field_id === 16;

                // Skip raw File objects; they are handled via uploadFiles. Also skip zero/empty placeholders for file fields.
                if (value?.constructor?.name === "File" || (Array.isArray(value) && value[0] instanceof File)) return;
                if (isFileField && (value === null || value === '' || Number(value) <= 0)) return;

                fieldValues[fieldId] = value;
            });

            // Merge uploaded file ids, letting uploads override any existing value
            Object.assign(fieldValues, uploadedFieldValues || {});

            if (Object.keys(fieldValues).length > 0) {
                const fieldsPayload = {
                    lead_id: caseData?.lead?.id,
                    case_id: caseData?.case?.id,
                    field_values: fieldValues
                };

                const fieldsResponse = await fetch('https://api.casedb.co/custom_fields.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(fieldsPayload),
                    credentials: 'include'
                });
                
                const fieldsResult = await fieldsResponse.json();
                if (!fieldsResponse.ok || fieldsResult.success === false) {
                    console.error('Error saving custom fields (including files):', fieldsResult);
                    return;
                }
            }

            // Wait a moment for the database to be updated
            await new Promise(resolve => setTimeout(resolve, 500));

            // Clear file objects from newData after successful upload
            const clearedData = { ...newData };
            Object.keys(clearedData).forEach(fieldId => {
                const value = clearedData[fieldId];
                if (value?.constructor?.name === "File" || (Array.isArray(value) && value[0] instanceof File)) {
                    delete clearedData[fieldId];
                }
            });

            setNewData(clearedData);
            setDataChanged(false);

            // Refresh custom fields to get updated data
            if (fetchFields) {
                fetchFields();
            }

            if (fetchCase) {
                fetchCase(true);
            }

            setAlert({
                type: 'success',
                message: 'Data saved successfully!'
            });
        } catch (error) {
            console.error('Error saving data:', error);
            setAlert({
                type: 'error',
                message: 'Error saving data. Please try again.'
            });
        }
    };

    useEffect(() => {
        setDataChanged(true);
    }, [leadData, newData]);

    console.log(caseTypes);

    return (
        <div className='case-section'>
            <TableOfContents
                key={2}
                subheaders={[{id: 0, name: 'Intake Information'}, {id: 1, name: 'Intake Details'}]}
                dataChanged={dataChanged}
                cancel={false}
                saveFields={saveData}
                onCancel={() => {
                    setDataChanged(false);
                }}
            />
            <Subheader title='Intake Information' />
            <div className='sub-section'>
                <div className='form-group nm'>
                    <label className='subtext'>Intake Date</label>
                    <input type='date' value={String(caseData?.lead?.created_at).split(" ")[0]} disabled />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Intake Time</label>
                    <input type='time' value={String(caseData?.lead?.created_at).split(" ")[1]} disabled />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Person Performing Intake</label>
                    <input type='text' value={caseData?.lead?.intake_by_name} disabled />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Marketing Source</label>
                    <Dropdown 
                        value={leadData?.marketing_source} 
                        marketing_list={true}
                        lead_filter={[1, 3]}
                        onChange={(val) => {
                            setLeadData({ ...leadData, marketing_source: val});
                            setDataChanged(true);
                        }} 
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Marketing Lead</label>
                    <Dropdown 
                        value={leadData?.marketing_lead} 
                        marketing_list={true}
                        lead_filter={2}
                        onChange={(val) => {
                            setLeadData({ ...leadData, marketing_lead: val});
                            setDataChanged(true);
                        }} 
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Referred By</label>
                    <Contact
                        selectedContact={leadData?.referral_contact}
                        setSelectedContact={(val) => {
                            setLeadData({ ...leadData, referral_contact: val});
                            setDataChanged(true);
                        }}
                        onCreateNewContact={() => {
                            setNewContactFieldId('referral_contact');
                            setCreateContact(true);
                        }}
                        onEditContact={handleEditContact}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>DB Origination</label>
                    <Contact
                        selectedContact={leadData?.db_origination}
                        setSelectedContact={(val) => {
                            setLeadData({ ...leadData, db_origination: val});
                            setDataChanged(true);
                        }}
                        onCreateNewContact={() => {
                            setNewContactFieldId('db_origination');
                            setCreateContact(true);
                        }}
                        onEditContact={handleEditContact}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Summary</label>
                    <textarea
                        value={leadData?.summary || ''}
                        onChange={(e) => {
                            setLeadData({ ...leadData, summary: e.target.value});
                            setDataChanged(true);
                        }}
                        placeholder="Summary..."
                    />
                </div>

                {fields.map((field) => (
                    <div key={field.id} id={`field-${field.id}`} className='form-group nm' style={{ scrollMarginTop: '120px' }}>
                        <label className='subtext'>{field.name}</label>
                        <input
                            type="date"
                            onChange={(e) =>
                                setNewData(prev => ({
                                        ...prev,
                                        [field.id]: e.target.value,
                                    }))
                            }
                            value={
                                newData?.[field.id] !== null ? newData?.[field.id] : fieldUpdates?.[field.id] || ''
                            }
                        />
                    </div>
                ))}
                <div className='form-group nm'>
                    <label className='subtext'>Case Type</label>
                    <Dropdown
                        options={caseTypes?.map(ct => [Number(ct.id), ct.name]) || []}
                        value={leadData.case_type_id}
                        onChange={handleCaseTypeChange}
                        placeholder="Select Case Type"
                    />
                </div>
            </div>
            <Subheader title='Intake Details' />
            <FieldGroup
                fields={customFields.filter(field => !unacceptedFields.includes(field.id) && !acceptedFields.includes(field.id))}
                formData={newData}
                fieldUpdates={fieldUpdates}
                lead_id={caseData?.lead?.id}
                case_id={caseData?.case?.id}
                onChange={(fId, val) => setNewData(prev => ({ ...prev, [fId]: val }))}
                caseType={leadData.case_type_id}
                sectionName="Intake Details"
                setNewContactFieldId={setNewContactFieldId}
                setCreateContact={setCreateContact}
                handleEditContact={handleEditContact}
                caseData={caseData}
            />
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    onContactCreated={handleNewContactCreated} 
                />
            )}
            {editContact && contactToEdit && (
                <EditDetail
                    setEditContact={setEditContact}
                    contactData={contactToEdit}
                    fetchContacts={fetchContacts}
                    setError={(error) => setAlert({ type: 'error', message: error })}
                    setCreateContact={setCreateContact}
                />
            )}
            {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)}/>}
        </div>
    );
}
